import { useEffect, useState, type ImgHTMLAttributes } from "react";
import { api } from "@/api/client";
import { useAuthStore } from "@/stores/auth";

interface SecureImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src"> {
  /** An application-owned API path; never a token or a public upload URL. */
  src: string;
  fallbackSrc?: string;
}

export function SecureImage({ src, fallbackSrc = "/placeholder-meal.svg", onError, ...props }: SecureImageProps) {
  const token = useAuthStore((state) => state.token);
  const [loaded, setLoaded] = useState<{ path: string; token: string; url: string } | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | undefined;
    setLoaded(null);
    if (token) {
      void api.get<Blob>(src, { responseType: "blob", signal: controller.signal })
        .then(({ data }) => {
          if (controller.signal.aborted) return;
          objectUrl = URL.createObjectURL(data);
          setLoaded({ path: src, token, url: objectUrl });
        })
        .catch(() => { /* Retain the public placeholder for unavailable photos. */ });
    }
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src, token]);

  // Guard at render time as well as in effect cleanup: no previous account's
  // object URL should appear for even one render after a session change.
  const visible = loaded?.path === src && loaded.token === token ? loaded.url : fallbackSrc;
  return <img {...props} src={visible} onError={(event) => {
    setLoaded(null);
    onError?.(event);
  }} />;
}
